import { Injectable, NgZone } from '@angular/core';
import { merge, fromEvent, Subscription } from 'rxjs';
import { AuthenticationService } from '../authentication/authentication.service';
import { ModalService } from './modal.service';
import { LogOutModalComponent } from '../../shared/modals/log-out-modal/log-out-modal.component';
import { LoggedUserService } from './logged-user.service';
import { JwtHelperService } from '@auth0/angular-jwt';

@Injectable({
  providedIn: 'root',
})
export class InactivityService {

  private inactivityTime = 0; // five minutes
  private intervalId: any;
  subscription: Subscription | undefined;
  targetDate: Date = new Date();

  constructor(
    private authService: AuthenticationService,
    private ngZone: NgZone,
    private modalService: ModalService,
    private loggedUserService: LoggedUserService,
    private jwtHelper: JwtHelperService
  ) {
    // Another tab extended the session: its prompt is already dismissed by the time
    // this runs, so all that is left is to count down again on the new expiry.
    this.authService.onSessionExtendedElsewhere = () => {
      this.ngZone.run(() => this.resetTimer());
    };
  }

  public startInactivityService() {

    this.setDateFromToken();
    this.ngZone.runOutsideAngular(() => {
      this.startTimer();
    });
  }

  startTimer() {
    // The service is a singleton started once per sign in, so drop any timer left
    // over from a previous session instead of running two.
    clearInterval(this.intervalId);

    this.intervalId = setInterval(async () => {
      const now = new Date();
      if (now >= this.targetDate) {
        await this.logOutFunction();
        clearInterval(this.intervalId);
      }
    }, 1000);
  }

  async logOutFunction() {
    this.ngZone.run(async () => {
      if (this.loggedUserService.loggedUser) {
        this.subscription?.unsubscribe();
        // this.resetTimer();
        // Dismissed rather than answered, because another tab settled the session
        // first. That tab drives what happens next -- logging out or refreshing
        // again from here would duplicate its work -- so this tab only waits for
        // the signal it broadcasts.
        const DISMISSED = Symbol('dismissed');
        const logout = await this.modalService.open(LogOutModalComponent)
          .catch(() => DISMISSED);

        if (logout === DISMISSED || !this.loggedUserService.loggedUser) {
          return;
        }

        if (logout) {
          this.authService.logout();
        } else {
          this.authService.refreshToken().subscribe(() => {
            this.resetTimer();
            // The other tabs are showing the same prompt and cleared their timers
            // when it opened; without this they would sit there with no countdown.
            this.authService.broadcastSessionExtended();
          });
        }
      }
    });
  }

  resetTimer() {
    this.setDateFromToken();
    clearInterval(this.intervalId);
    this.startTimer();
  }

  setDateFromToken() {
    let token = localStorage.getItem('access_token');

    if (token) {
      const expirationDate = this.jwtHelper.getTokenExpirationDate(token);

      console.log('Expiration date: ', expirationDate?.toISOString());

      if (expirationDate) {
        this.targetDate = expirationDate;
        this.targetDate.setSeconds(expirationDate.getSeconds() - 30);
      }
    }
  }
}
